import { useCallback } from 'react';

import {
	type GetAllPromptsResponse,
	type GetPromptResponse,
	type PromptCategory,
	type PromptRole,
} from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import {
	type AssistanceServiceInjectedParams,
	type AssistanceServiceInputParam,
	createHookWithAssistanceService,
} from '@atlassian/conversation-assistant-service/services/assistance-service/main';
import type { CreatePromptFormData } from '@atlassian/rovo-conversation-actions/create-prompt-modal';

import { useCreatePromptController } from './useCreatePromptController';

export type UseCreatePromptProps = AssistanceServiceInjectedParams &
	AssistanceServiceInputParam & {
		/** Call after a prompt is created to refresh the prompts list. Optional when used from sidebar or other surfaces that don't show the list. */
		refetchPrompts?: () => Promise<GetAllPromptsResponse | null>;
	};

export type UseCreatePromptResult = {
	handleCreatePrompt: (data: CreatePromptFormData) => Promise<GetPromptResponse>;
};

export const useCreatePrompt: (
	props: Omit<UseCreatePromptProps, 'assistanceServiceInjectedParams'> &
		AssistanceServiceInputParam,
) => UseCreatePromptResult = createHookWithAssistanceService(
	({
		assistanceServiceInjectedParams,
		refetchPrompts,
	}: UseCreatePromptProps): UseCreatePromptResult => {
		const assistanceServiceParams = {
			touchpointSource: assistanceServiceInjectedParams.touchpointSource,
			product: assistanceServiceInjectedParams.product,
			experienceId: assistanceServiceInjectedParams.experienceId,
			siteId: assistanceServiceInjectedParams.siteId,
			headers: assistanceServiceInjectedParams.headers,
			baseUrl: assistanceServiceInjectedParams.baseUrl,
		};
		const { createPrompt } = useCreatePromptController({
			assistanceServiceParams,
		});
		const handleCreatePrompt = useCallback(
			async (data: CreatePromptFormData) => {
				const result = await createPrompt({
					title: data.title.trim(),
					prompt: data.prompt.trim(),
					...(data.useCase?.value && {
						useCase: data.useCase.value as PromptCategory,
					}),
					roles:
						data.role.length > 0
							? data.role.map((r) => r.value as PromptRole)
							: (['General'] as PromptRole[]),
					...(data.app.length > 0 && {
						appIds: data.app.map((a) => a.value),
					}),
				});
				await refetchPrompts?.();
				return result;
			},
			[createPrompt, refetchPrompts],
		);
		return { handleCreatePrompt };
	},
);
